import type { ContentType, ResizeMode, StreamItem } from './stream';
import { toStampMs } from '../helpers/datetime';

// Normalized content row persisted in IndexedDB. Mirrors ContentRow.kt.
export interface ContentRow {
  uuid: string;
  position: number;
  duration: number; // seconds
  startMs: number;
  endMs: number; // 0 = no end
  type: ContentType;
  resizeMode: ResizeMode;
  sequence: number;

  fileName: string;
  fileType: string;
  fileSize: number;
  fileUrl: string;

  // Remote source for non-file content types (e.g. url -> iframe).
  sourceUrl: string;

  qrShow: boolean;
  qrPosition: string;
  qrSize: string;
  qrData: string;

  logAd: number;
  logDevice: number;
  logLocation: number;
  logSubscription: number;
}

// Stored display-log entry (queued until synced). Mirrors LogRow.kt.
export interface LogRow {
  id?: number;
  ad: number;
  device: number;
  location: number;
  subscription: number;
  startMs: number;
  endMs: number;
  duration: number;
}

// Media blob keyed by file name.
export interface MediaRow {
  name: string;
  size: number;
  blob: Blob;
}

export function streamItemToContent(item: StreamItem, sequence: number): ContentRow {
  return {
    uuid: item.uuid,
    position: item.order,
    duration: item.duration,
    startMs: toStampMs(item.start_date),
    endMs: toStampMs(item.end_date),
    type: item.type,
    resizeMode: (item.resize_mode ?? 'fill') as ResizeMode,
    sequence,

    fileName: item.file?.name ?? '',
    fileType: item.file?.type ?? '',
    fileSize: item.file?.size ?? 0,
    fileUrl: item.file?.url ?? '',

    sourceUrl: item.url ?? item.file?.url ?? '',

    qrShow: item.qr?.show ?? false,
    qrPosition: item.qr?.position ?? 'bottom|right',
    qrSize: item.qr?.size ?? 'small',
    qrData: item.qr?.data ?? '',

    logAd: item.log_data?.ad_id ?? 0,
    logDevice: item.log_data?.device_id ?? 0,
    logLocation: item.log_data?.location_id ?? 0,
    logSubscription: item.log_data?.subscription_id ?? 0,
  };
}

// True when meaningful fields differ (used to decide playlist reload).
export function contentChanged(a: ContentRow, b: ContentRow): boolean {
  return (
    a.uuid !== b.uuid ||
    a.position !== b.position ||
    a.duration !== b.duration ||
    a.startMs !== b.startMs ||
    a.endMs !== b.endMs ||
    a.type !== b.type ||
    a.resizeMode !== b.resizeMode ||
    a.fileName !== b.fileName ||
    a.fileType !== b.fileType ||
    a.fileSize !== b.fileSize ||
    a.sourceUrl !== b.sourceUrl ||
    a.qrShow !== b.qrShow ||
    a.qrPosition !== b.qrPosition ||
    a.qrSize !== b.qrSize ||
    a.qrData !== b.qrData
  );
}
